import React from 'react';
import { withStyles } from '@material-ui/styles';
import { Radio, Checkbox } from '@material-ui/core/';
import Utils from '../../utils/Utils';

// Конфигурируеми групи добавки на един продукт ("Протеин - избери 1", "Зеленчуци - избери
// до 3, с количества"). Напълно отделно от старите required/required2/addable секции,
// които си остават както са - продукт без групи изобщо не рендерира този компонент.
//
// Стойността (`value`) е речник по groupId: { "12": [{ id: 45, qty: 2 }], "13": [{ id: 51, qty: 1 }] }
// - същата структура, която отива в количката и в заявката към сървъра.
const ACCENT = 'rgb(254, 105, 2)';

const styles = theme => ({
    groupBlock: {
        marginBottom: 18,
    },
    groupHeader: {
        background: 'rgba(254, 105, 2, 0.33)',
        padding: '8px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    groupTitle: {
        flex: 1,
        margin: 0,
        fontWeight: 600,
        textAlign: 'left',
    },
    counter: {
        fontSize: '0.8em',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 10,
        background: '#fff',
        color: '#555',
        whiteSpace: 'nowrap',
    },
    counterComplete: {
        background: '#e6f6e8',
        color: '#1b7a2c',
    },
    requiredBadge: {
        fontSize: '0.7em',
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: 10,
        background: ACCENT,
        color: '#fff',
        whiteSpace: 'nowrap',
    },

    // --- Режим "Списък": един на ред, ляво подравнено, без центриране ---
    listRow: {
        display: 'flex',
        alignItems: 'center',
        padding: '6px 4px',
        borderBottom: '1px solid #f0f0f0',
        textAlign: 'left',
        cursor: 'pointer',
    },
    listRowDisabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
    },
    rowMain: {
        flex: 1,
        minWidth: 0,
        paddingLeft: 4,
    },
    rowName: {
        display: 'block',
        lineHeight: 1.25,
    },
    rowMeta: {
        display: 'block',
        fontSize: '0.75em',
        color: '#888',
        lineHeight: 1.3,
    },
    rowPrice: {
        whiteSpace: 'nowrap',
        fontWeight: 600,
        marginLeft: 8,
    },

    // --- Режим "Чипове": компактни pill бутони, които се пренасят ---
    chipsWrap: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        padding: '8px 4px',
    },
    chip: {
        border: '1px solid #d5d5d5',
        borderRadius: 18,
        padding: '5px 12px',
        fontSize: '0.85em',
        cursor: 'pointer',
        background: '#fff',
        lineHeight: 1.3,
        userSelect: 'none',
    },
    chipSelected: {
        borderColor: ACCENT,
        background: 'rgba(254, 105, 2, 0.12)',
        fontWeight: 600,
    },
    chipDisabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
    },

    // --- Режим "Както старите добавки" ---
    legacyWrap: {
        display: 'block',
        textAlign: 'left',
    },

    // Степер за количество
    stepper: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: 8,
    },
    stepperBtn: {
        width: 26,
        height: 26,
        lineHeight: '24px',
        textAlign: 'center',
        border: '1px solid ' + ACCENT,
        borderRadius: '50%',
        color: ACCENT,
        fontWeight: 700,
        cursor: 'pointer',
        userSelect: 'none',
        background: '#fff',
        padding: 0,
    },
    stepperBtnDisabled: {
        opacity: 0.35,
        cursor: 'not-allowed',
    },
    stepperValue: {
        minWidth: 24,
        textAlign: 'center',
        fontWeight: 700,
    },
});

class IngredientGroupsSelector extends React.Component {

    // --- четене на текущия избор -------------------------------------------------

    picksFor = (groupId) => {
        const value = this.props.value || {};
        const picks = value[groupId];
        if (!Array.isArray(picks)) return [];
        return picks;
    }

    pickOf = (groupId, ingredientId) => {
        return this.picksFor(groupId).find(p => parseInt(p.id) === parseInt(ingredientId));
    }

    unitsIn = (groupId) => {
        return this.picksFor(groupId).reduce((sum, p) => sum + (parseInt(p.qty) || 1), 0);
    }

    maxOf = (group) => {
        if (group.max_select === null || typeof group.max_select == 'undefined' || group.max_select === '') return null;
        const max = parseInt(group.max_select);
        return max > 0 ? max : null;
    }

    maxPerItemOf = (group) => {
        if (!group.allow_quantity) return 1;
        const max = parseInt(group.max_per_item);
        return max > 0 ? max : 1;
    }

    // При достигнат максимум неизбраните се затъмняват и не се цъкат. Избраните остават
    // активни, за да може човек да махне и да смени.
    //
    // ИЗКЛЮЧЕНИЕ: група с максимум 1 се държи като истински радио бутон - цъкаш друг и той
    // просто заменя избрания. Иначе, щом единият е избран, всички останали щяха да са
    // "запълнили максимума" и щеше да се налага първо да отчекнеш и после да чекнеш.
    isOptionLocked = (group, ingredientId) => {
        const max = this.maxOf(group);
        if (max === null || max === 1) return false;
        if (this.pickOf(group.id, ingredientId)) return false;
        return this.unitsIn(group.id) >= max;
    }

    // --- промяна на избора -------------------------------------------------------

    emit = (groupId, picks) => {
        const next = { ...(this.props.value || {}) };
        if (picks.length === 0) {
            delete next[groupId];
        } else {
            next[groupId] = picks;
        }
        this.props.onChange(next);
    }

    toggleOption = (group, ingredientId) => {
        const max = this.maxOf(group);
        const existing = this.pickOf(group.id, ingredientId);

        // Единичен избор (радио): новият заменя стария.
        if (max === 1) {
            if (existing && parseInt(group.min_select) === 0) {
                this.emit(group.id, []);
                return;
            }
            this.emit(group.id, [{ id: parseInt(ingredientId), qty: 1 }]);
            return;
        }

        if (existing) {
            this.emit(group.id, this.picksFor(group.id).filter(p => parseInt(p.id) !== parseInt(ingredientId)));
            return;
        }

        if (this.isOptionLocked(group, ingredientId)) return;

        this.emit(group.id, this.picksFor(group.id).concat([{ id: parseInt(ingredientId), qty: 1 }]));
    }

    changeQty = (group, ingredientId, delta) => {
        const picks = this.picksFor(group.id);
        const existing = picks.find(p => parseInt(p.id) === parseInt(ingredientId));
        if (!existing) return;

        const currentQty = parseInt(existing.qty) || 1;
        const nextQty = currentQty + delta;

        if (nextQty < 1) {
            this.emit(group.id, picks.filter(p => parseInt(p.id) !== parseInt(ingredientId)));
            return;
        }
        if (nextQty > this.maxPerItemOf(group)) return;

        // Бройките се броят към максимума на групата (2x кюфте = 2 от 3).
        const max = this.maxOf(group);
        if (max !== null && delta > 0 && this.unitsIn(group.id) + delta > max) return;

        this.emit(group.id, picks.map(p =>
            parseInt(p.id) === parseInt(ingredientId) ? { ...p, qty: nextQty } : p
        ));
    }

    // --- текстове ----------------------------------------------------------------

    groupTitle = (group) => {
        const currLang = this.props.currLang;
        if (group.translations && group.translations[currLang] && group.translations[currLang].name) {
            return group.translations[currLang].name;
        }
        return group.name || '';
    }

    // Цената на реда следва избраната бройка: 2x по 2.60 се показва като "+ 5.20", за да
    // отговаря на това, което наистина се добавя към сметката.
    priceLabel = (ingredient, qty = 1) => {
        const settings = this.props.settings || {};
        if (!ingredient || !ingredient.price || parseFloat(ingredient.price) === 0) return '';

        const suffix = settings.default_currency_suffix && settings.default_currency_suffix.value
            ? settings.default_currency_suffix.value
            : 'лв.';

        const total = (parseFloat(ingredient.price) * (qty > 0 ? qty : 1)).toFixed(2);

        let label = '+ ' + total + ' ' + suffix;
        if (Utils.getSettingsValue(settings, 'show_prices_in_two_currency') == '1') {
            label += ' / ' + Utils.showPricesInEuroCurrency(total);
        }
        return label;
    }

    // "330 kcal · 62 г протеин" - показва се само каквото съставката наистина има.
    nutritionLabel = (ingredient) => {
        if (!ingredient) return '';

        const parts = [];
        if (ingredient.kcal != null && !isNaN(parseFloat(ingredient.kcal)) && parseFloat(ingredient.kcal) > 0) {
            parts.push(parseFloat(ingredient.kcal) + ' kcal');
        }
        if (ingredient.protein != null && !isNaN(parseFloat(ingredient.protein)) && parseFloat(ingredient.protein) > 0) {
            parts.push(parseFloat(ingredient.protein) + ' г протеин');
        }
        return parts.join(' · ');
    }

    counterLabel = (group) => {
        const units = this.unitsIn(group.id);
        const max = this.maxOf(group);
        return max === null ? String(units) : units + '/' + max;
    }

    isGroupComplete = (group) => {
        const units = this.unitsIn(group.id);
        const min = parseInt(group.min_select) || 0;
        const max = this.maxOf(group);
        if (min > 0 && units < min) return false;
        return max !== null ? units >= max : units > 0;
    }

    // --- рендер ------------------------------------------------------------------

    renderStepper = (group, ingredientId) => {
        const { classes } = this.props;
        const pick = this.pickOf(group.id, ingredientId);
        if (!group.allow_quantity || !pick) return null;

        const qty = parseInt(pick.qty) || 1;
        const max = this.maxOf(group);
        const atItemMax = qty >= this.maxPerItemOf(group);
        const atGroupMax = max !== null && this.unitsIn(group.id) >= max;
        const plusDisabled = atItemMax || atGroupMax;

        return (
            <span className={classes.stepper} onClick={e => e.stopPropagation()}>
                <button type="button" className={classes.stepperBtn}
                    onClick={() => this.changeQty(group, ingredientId, -1)}>−</button>
                <span className={classes.stepperValue}>{qty}</span>
                <button type="button"
                    className={classes.stepperBtn + (plusDisabled ? ' ' + classes.stepperBtnDisabled : '')}
                    disabled={plusDisabled}
                    onClick={() => this.changeQty(group, ingredientId, 1)}>+</button>
            </span>
        );
    }

    renderListRow = (group, option) => {
        const { classes, productIngredients } = this.props;
        const ingredient = productIngredients && productIngredients[option.ingredientId];
        if (!ingredient) return null;

        const pick = this.pickOf(group.id, option.ingredientId);
        const selected = !!pick;
        const locked = this.isOptionLocked(group, option.ingredientId);
        const isRadio = this.maxOf(group) === 1;
        const meta = this.nutritionLabel(ingredient);
        const price = this.priceLabel(ingredient, pick ? (parseInt(pick.qty) || 1) : 1);

        return (
            <div key={option.ingredientId}
                data-class="ingredient-group-row"
                className={classes.listRow + (locked ? ' ' + classes.listRowDisabled : '')}
                onClick={() => { if (!locked) this.toggleOption(group, option.ingredientId); }}>

                {isRadio ?
                    <Radio checked={selected} disabled={locked} style={{ color: ACCENT, padding: 6 }} />
                    :
                    <Checkbox checked={selected} disabled={locked} style={{ color: ACCENT, padding: 6 }} />
                }

                <span className={classes.rowMain}>
                    <span className={classes.rowName}>{ingredient.name}</span>
                    {meta ? <span className={classes.rowMeta}>{meta}</span> : null}
                </span>

                {price ? <span className={classes.rowPrice}>{price}</span> : null}
                {this.renderStepper(group, option.ingredientId)}
            </div>
        );
    }

    renderChip = (group, option) => {
        const { classes, productIngredients } = this.props;
        const ingredient = productIngredients && productIngredients[option.ingredientId];
        if (!ingredient) return null;

        const pick = this.pickOf(group.id, option.ingredientId);
        const selected = !!pick;
        const locked = this.isOptionLocked(group, option.ingredientId);
        const qty = pick ? (parseInt(pick.qty) || 1) : 1;
        const price = this.priceLabel(ingredient, qty);

        return (
            <span key={option.ingredientId}
                data-class="ingredient-group-chip"
                className={classes.chip
                    + (selected ? ' ' + classes.chipSelected : '')
                    + (locked ? ' ' + classes.chipDisabled : '')}
                onClick={() => { if (!locked) this.toggleOption(group, option.ingredientId); }}>
                {ingredient.name}
                {selected && group.allow_quantity && qty > 1 ? ' x' + qty : ''}
                {price ? ' (' + price + ')' : ''}
                {this.renderStepper(group, option.ingredientId)}
            </span>
        );
    }

    renderGroup = (group) => {
        const { classes } = this.props;
        const options = (group.options || []).filter(opt => {
            const ingredient = this.props.productIngredients && this.props.productIngredients[opt.ingredientId];
            return ingredient && ingredient.active != false;
        });

        if (options.length === 0) return null;

        const min = parseInt(group.min_select) || 0;
        const complete = this.isGroupComplete(group);

        return (
            <div className={classes.groupBlock} key={group.id} data-class="ingredient-group">
                <div className={classes.groupHeader}>
                    <p data-class="ingredient-group-title" className={classes.groupTitle}>{this.groupTitle(group)}</p>
                    {min > 0 ?
                        <span className={classes.requiredBadge}>
                            {this.props.requiredLabel || 'Задължително'}
                        </span>
                        : null}
                    <span className={classes.counter + (complete ? ' ' + classes.counterComplete : '')}>
                        {this.counterLabel(group)}
                    </span>
                </div>

                {group.layout === 'chips' ?
                    <div className={classes.chipsWrap}>
                        {options.map(opt => this.renderChip(group, opt))}
                    </div>
                    :
                    <div className={group.layout === 'legacy' ? classes.legacyWrap : null}>
                        {options.map(opt => this.renderListRow(group, opt))}
                    </div>
                }
            </div>
        );
    }

    render() {
        const groups = this.props.groups || [];
        if (groups.length === 0) return null;

        return (
            <div data-class="ingredient-groups-container">
                {groups.map(this.renderGroup)}
            </div>
        );
    }
}

export default withStyles(styles)(IngredientGroupsSelector);
